import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';
import { Grid } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { selectMiscellaneousDialogProps } from '../PlacementDetails/Edit/store/placementPageAttributes.selector';
import { customSwitch } from 'utils/customSwitchCase/customSwitch';
import { MiscPlacementDialogType as DialogType } from 'app/models/Placement/PlacementDetails';
import { pageAttributesActions } from '../PlacementDetails/Edit/store/placementPageAttributes.redux';
import { placementStatusAction } from 'store/redux-store/placement-status/slice';
import { useNavigate, useLocation } from 'react-router-dom';
import { redirectToCandidateContractDocEditor } from '../CreateCandidateContractDoc/utils';

const useStyles = makeStyles()(theme => ({
  dialogContainer: {
    width: '500px',
    maxWidth: '500px',
  },
}));

export const MiscDialogues = () => {
  const { classes } = useStyles();

  const { t } = useTranslation();

  const dialogProps = useSelector(selectMiscellaneousDialogProps);

  const history = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const title = React.useMemo(
    () =>
      customSwitch({
        [DialogType.on_assignment_confirmation_cancellation]: t(
          'placement.profile.miscDialogue.onAssignmentCancellation.title',
        ),
        [DialogType.generate_candidate_contract]: t('placement.profile.miscDialogue.generateCandidateContract.title'),
      })(null)(dialogProps?.key),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialogProps?.key],
  );

  const handleClose = React.useCallback(
    () => dispatch(pageAttributesActions.closeMiscDialogue()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleAssignmentCancellation = React.useCallback(() => {
    handleClose();
    dispatch(placementStatusAction.setSelectedStatus({ ...dialogProps?.data }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogProps?.data]);

  const handleContractGeneration = React.useCallback(() => {
    handleClose();
    redirectToCandidateContractDocEditor(history, location, { defaultSelection: 'draft' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const dialogActions = React.useMemo(
    () =>
      customSwitch({
        [DialogType.on_assignment_confirmation_cancellation]: [
          {
            onClick: handleClose,
            text: t('No'),
            variant: 'text',
            color: 'tertiary',
            disabled: false,
          },
          {
            onClick: handleAssignmentCancellation,
            text: 'Yes',
            variant: 'contained',
            loading: false,
            disabled: false,
          },
        ],
        [DialogType.generate_candidate_contract]: [
          {
            onClick: handleClose,
            text: t('No'),
            variant: 'text',
            color: 'tertiary',
            disabled: false,
          },
          {
            onClick: handleContractGeneration,
            text: t('Yes'),
            variant: 'contained',
            loading: false,
            disabled: false,
          },
        ],
      })([])(dialogProps?.key),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialogProps?.key, handleAssignmentCancellation, t],
  );

  const dialgContent = React.useMemo(
    () =>
      customSwitch({
        [DialogType.on_assignment_confirmation_cancellation]: (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {t('placement.profile.miscDialogue.onAssignmentCancellation.message')}
            </Grid>
          </Grid>
        ),
        [DialogType.generate_candidate_contract]: (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {t('placement.profile.miscDialogue.generateCandidateContract.message')}
            </Grid>
          </Grid>
        ),
      })(null)(dialogProps?.key),
    [dialogProps?.key, t],
  );

  return !dialogProps?.key ? null : (
    <GenericDialog
      variant="white"
      fullWidth
      open
      draggable
      classes={{
        paper: classes.dialogContainer,
      }}
      dialogTitleProps={{
        text: title,
        closeButton: true,
      }}
      dialogActions={dialogActions}
      onClose={handleClose}
    >
      {dialgContent}
    </GenericDialog>
  );
};
